import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { SEARCH_FRESH_MS } from '../constants/search';

/**
 * Memoria de la última URL visitada por ruta, para que la barra de navegación
 * devuelva al agente a SU búsqueda y no a un formulario vacío.
 *
 * Sin esto, ir a "Autos" y volver a "Traslados" navega a `/transfers` pelado:
 * la búsqueda vive en los query params, así que perderlos es perder la
 * búsqueda — y de paso el resultado cacheado, que nunca se llega a consultar.
 *
 * La edad de esta memoria limita cuánto tiempo se restauran automáticamente
 * esos parámetros. NO demuestra que exista un resultado cacheado ni que una
 * cotización siga vigente: eso lo decide React Query con `dataUpdatedAt` y la
 * autoridad local de la generación mostrada. Si el cache no existe, montar la
 * URL recordada puede ejecutar una búsqueda en vivo.
 *
 * Va en `sessionStorage` a propósito: es por pestaña y muere al cerrarla. Una
 * pestaña nueva arranca limpia, y dos pestañas no se pisan la navegación. Las
 * búsquedas que SÍ tienen que sobrevivir son las recientes, que son otra cosa
 * y viven aparte.
 */

const KEY_PREFIX = 'nav:lastSearch:';

interface Remembered {
  search: string;
  at: number;
}

function read(pathname: string): Remembered | null {
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + pathname);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof (parsed as Remembered).search !== 'string' ||
      typeof (parsed as Remembered).at !== 'number'
    ) {
      // Formato viejo (la URL pelada) o storage editado: sin memoria.
      return null;
    }
    return parsed as Remembered;
  } catch {
    // Modo incógnito estricto / storage bloqueado: navegar sin memoria es
    // exactamente el comportamiento anterior, no un error.
    return null;
  }
}

function write(pathname: string, search: string): void {
  try {
    if (search) {
      const entry: Remembered = { search, at: Date.now() };
      sessionStorage.setItem(KEY_PREFIX + pathname, JSON.stringify(entry));
    } else {
      sessionStorage.removeItem(KEY_PREFIX + pathname);
    }
  } catch {
    /* sin memoria: ver arriba */
  }
}

/**
 * Olvida la búsqueda de una ruta. Lo va a usar "Limpiar": vaciar el formulario
 * sin olvidar esto sería mentir — el agente se va a otro vertical, vuelve, y la
 * búsqueda que acaba de limpiar reaparece.
 */
export function forgetRoute(pathname: string): void {
  try {
    sessionStorage.removeItem(KEY_PREFIX + pathname);
  } catch {
    /* sin memoria: ver arriba */
  }
}

/**
 * Devuelve un navegador que restaura por un tiempo los query params con los que
 * se dejó cada ruta. Registra la ubicación actual en cada cambio, así que hay
 * que usarlo desde un componente montado siempre (la barra de navegación).
 */
export function useNavigateRemembering() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    write(location.pathname, location.search);
  }, [location.pathname, location.search]);

  return useCallback(
    (route: string, context: Record<string, string | null> = {}) => {
      // Si ya estamos en la ruta, no se re-navega: tocar la tab activa no
      // puede revivir una búsqueda que el agente acaba de limpiar.
      if (location.pathname === route) return;
      const remembered = read(route);
      const withinMemoryWindow = remembered && Date.now() - remembered.at < SEARCH_FRESH_MS;
      const params = new URLSearchParams(withinMemoryWindow ? remembered.search : '');
      Object.entries(context).forEach(([key, value]) => {
        if (value) params.set(key, value);
        else params.delete(key);
      });
      const search = params.toString();
      navigate(`${route}${search ? `?${search}` : ''}`);
    },
    [navigate, location.pathname],
  );
}
